import { DurationUnit } from "luxon";

import { Individual } from "@/Common/Api/Individuals/Dto/Individual";
import { LuxonDateHelper } from "@/Common/Helpers/LuxonDateHelper";
import { MathHelper } from "@/Common/Helpers/MathHelper";
import { UnitSystem } from "@/Common/Models/Patient/PatientEnums";
import { BaseChartModel } from "@/Patient/Models/GrowthCharts/BaseChartModel";
import { AreaLabelConfiguration, ChartDataPoint } from "@/Patient/Models/GrowthCharts/ChartData";
import { CurvesName, GrowthChartsInterpolation } from "@/Patient/Models/GrowthCharts/LoadGrowthCurvesCacheResponse";
import { MedicalItemVitalSign } from "@/Patient/Models/MedicalSummary/MedicalItemVitalSign";
import { CorrectedAgeCalculator } from "@/Patient/Services/GrowthCharts/Calculators/CorrectedAgeCalculator";
import { PercentileCalculator } from "@/Patient/Services/GrowthCharts/Calculators/PercentileCalculator";
import { AxisLine, IGrowthChartGraph, IGrowthChartGraphConfig } from "../../Charts/GrowthChartGraph";
import { IGrowthChartsCustomAreaHandler } from "../../Charts/IGrowthChartsCustomAreaHandler";
import { GrowthChartsData } from "../GrowthChartsSelector";

export abstract class GrowthChartModel extends BaseChartModel implements IGrowthChartGraph {
    public constructor(
        patient: Individual,
        vitals: Immutable.List<MedicalItemVitalSign>,
        chartInterpolations: { [key: string]: GrowthChartsInterpolation[] },
        zScores: { [key: number]: { [key: number]: number } },
        unit?: UnitSystem
    ) {
        super(patient, vitals, unit);
        this.data = this.convertToGrowthChartsData(vitals, chartInterpolations, zScores);
    }

    public get growthChartsCustomAreaHandler(): IGrowthChartsCustomAreaHandler {
        return null;
    }

    public get hasCustomRegions(): boolean {
        return false;
    }

    public get hideXAxisGrid(): boolean {
        return false;
    }

    public abstract get curveName(): CurvesName;

    public readonly data: GrowthChartsData[];

    public get isPlotSelectionSupported(): boolean {
        return this.patient.IsBornPremature && !!this.patient.BornPrematureWeeks;
    }

    public isCompatible(): boolean {
        return !!this.patient.DateOfBirth && this.patient.GenderLookupId != null && this.patient.GenderLookupId != "";
    }

    public get primary(): IGrowthChartGraphConfig {
        return {
            dataPoints: this.chronologicalAgeDataPoints(),
            name: {
                xName: `${this.id}_primary_x`,
                yName: `${this.id}_primary_y`
            },
            color: "#1E355C"
        };
    }

    public get hasLengthAsIndependentAxis(): boolean {
        return false;
    }

    public get secondary(): IGrowthChartGraphConfig {
        if (!this.isPlotSelectionSupported)
            return null;

        return {
            dataPoints: this.correctedAgeDataPoints(),
            name: {
                xName: `${this.id}_secondary_x`,
                yName: `${this.id}_secondary_y`
            },
            color: "#29abe2"
        };
    }

    public getLines(): AxisLine[] {
        if (this.ageUnit !== "month")
            return [];

        const className = "grid-line-label";
        const year = Math.floor(this.xAxis.max / 12);

        const lines = [];
        for (let i = 1; i <= year; i++) {
            lines.push({
                label: `${i} ${cacheStore.GetString(i === 1 ? "YearLabel" : "YearsLabel").toLowerCase()}`,
                value: 12 * i,
                className: className
            });
        }

        return lines;
    }

    public getRegionLabels(): AreaLabelConfiguration[] {
        return [];
    }

    protected chronologicalAgeDataPoints(): ChartDataPoint[] {
        return this.data.map(p => ({
            id: p.id,
            x: p.chronologicalAge + "",
            y: p.vitalSignValue,
            tooltipValue: p.chronologicalPercentile ? p.chronologicalPercentile + "%" : "",
            medicalItemId: p.medicalItemId
        }));
    }

    protected correctedAgeDataPoints(): ChartDataPoint[] {
        return this.data.map(p => ({
            id: p.id,
            x: p.correctedAge + "",
            y: p.vitalSignValue,
            tooltipValue: p.correctedPercentile ? p.correctedPercentile + "%" : "",
            medicalItemId: p.medicalItemId
        }));
    }

    private convertToGrowthChartsData(vitalSigns: Immutable.List<MedicalItemVitalSign>, interpolation: { [key: string]: GrowthChartsInterpolation[] }, zScores: { [key: number]: { [key: number]: number } }): GrowthChartsData[] {
        const dataRows: GrowthChartsData[] = [];
        if (!this.isCompatible())
            return dataRows;

        const percentileCalculator = (interpolation && zScores) ? new PercentileCalculator(interpolation, zScores) : null;
        const correctedAgeDate = !!this.patient.BornPrematureWeeks ?
            CorrectedAgeCalculator.calculateCorrectedAge(this.patient.DateOfBirth, this.patient.BornPrematureWeeks) :
            null;

        const filteredVitalSigns = vitalSigns.filter(vitalSign => this.filterVitalSign(vitalSign));
        filteredVitalSigns?.forEach(vitalSign => {
            const field = vitalSign[this.vitalProperty];
            if (field) {
                const data: GrowthChartsData = {
                    id: `${this.id}_${vitalSign.Id}`,
                    medicalItemId: vitalSign.MedicalItemId,
                    date: vitalSign.PharmaStartDate,
                    vitalSignValue: MathHelper.roundToDecimals(this.convertVitalSignIfNeeded(vitalSign[this.vitalProperty], vitalSign.WeightUnitLookupId, vitalSign.HeightUnitLookupId, vitalSign.HeadCircumferenceUnitLookupId), 2).toString(),
                    chronologicalAge: this.getChronologicalTimeValue(vitalSign),
                    chronologicalPercentile: percentileCalculator?.getPercentile(
                        this.patient.DateOfBirth,
                        vitalSign,
                        this.vitalProperty,
                        this.curveName,
                        this.isGramsConversionRequired(),
                        this.patient.BornPrematureWeeks
                    )
                };

                if (this.patient.IsBornPremature && this.patient.BornPrematureWeeks) {
                    if (this.isFentonChart) {
                        data.chronologicalAge += this.patient.BornPrematureWeeks;
                    }
                    else if (this.isPlotSelectionSupported) {
                        const correctedAgeDiff = this.getCorrectedTimeValue(vitalSign, correctedAgeDate);
                        if (correctedAgeDiff >= 0) {
                            data.correctedAge = correctedAgeDiff;
                            data.correctedPercentile = percentileCalculator?.getPercentile(
                                correctedAgeDate,
                                vitalSign,
                                this.vitalProperty,
                                this.curveName,
                                this.isGramsConversionRequired(),
                                this.patient.BornPrematureWeeks
                            );
                        }
                    }
                }

                dataRows.push(data);
            }
        });

        return dataRows.sortByProperty(p => p.date);
    }

    private isGramsConversionRequired(): boolean {
        return this.isFentonChart && this.vitalProperty === "Weight";
    }

    private getChronologicalTimeValue(vitalSign: MedicalItemVitalSign): number {
        if (this.hasLengthAsIndependentAxis) {
            return this.unit === UnitSystem.Metric ?
                new MedicalItemVitalSign(vitalSign).getHeightInCentimeters() :
                new MedicalItemVitalSign(vitalSign).getHeightInInches();
        }
        return LuxonDateHelper.dateDiff(this.patient.DateOfBirth, vitalSign.PharmaStartDate, (`${this.ageUnit}s`) as DurationUnit);
    }

    private getCorrectedTimeValue(vitalSign: MedicalItemVitalSign, correctedAgeDate: Date): number {
        return LuxonDateHelper.dateDiff(correctedAgeDate, vitalSign.PharmaStartDate, (`${this.ageUnit}s`) as DurationUnit);
    }
}
